// ============================================================
//  AuthContext — the single auth-state provider for the SPA.
//  Wraps Firebase onAuthStateChanged so every route can read the
//  current user synchronously, and exposes the auth actions the
//  login screen needs. Proves the auth -> protected-route pattern.
// ============================================================

import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  signInWithPopup,
  GoogleAuthProvider,
  EmailAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  deleteUser,
  getAdditionalUserInfo,
  sendPasswordResetEmail,
  signOut as fbSignOut,
} from 'firebase/auth';
import { auth } from '../firebase.js';
import { createUserProfile } from '../data/profiles.js';
import { triggerWelcomeEmail } from '../data/welcomeEmail.js';
import posthog, { posthogEnabled } from '../posthog.js';

const AuthContext = createContext(null);

// Convert Firebase error codes to friendly text (mirrors www/firebase.js
// Auth._friendlyError, trimmed to the codes this Phase-0 login can hit).
function friendlyError(code) {
  const map = {
    'auth/user-not-found': 'No account found with this email.',
    'auth/wrong-password': 'Incorrect password. Please try again.',
    'auth/invalid-credential': 'Incorrect email or password. Please try again.',
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/email-already-in-use': 'An account with this email already exists. Try signing in.',
    'auth/weak-password': 'Password is too weak. Use at least 6 characters.',
    'auth/too-many-requests': 'Too many attempts. Please wait and try again.',
    'auth/network-request-failed': 'Network error. Check your connection.',
    'auth/popup-closed-by-user': 'Sign in cancelled.',
    'auth/popup-blocked':
      'Your browser blocked the sign-in window. Allow pop-ups for this site, or use email sign-in.',
    'auth/unauthorized-domain':
      'This domain is not authorised for Google sign-in. Add it in Firebase Auth > Settings > Authorized domains (localhost works by default).',
    'auth/requires-recent-login': 'For your security, please sign in again and retry.',
    'auth/user-mismatch': 'Those credentials are for a different account.',
  };
  return map[code] || 'Something went wrong. Please try again.';
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  // `initialising` is true until the first onAuthStateChanged fires, so
  // protected routes don't bounce a logged-in user to /login on a hard refresh
  // before Firebase has restored the persisted session.
  const [initialising, setInitialising] = useState(true);
  // Keeps the anonymous session intact until Firebase confirms a user, while
  // ensuring account changes start with a clean analytics identity.
  const identifiedUserId = useRef(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (posthogEnabled) {
        if (u?.uid) {
          if (identifiedUserId.current && identifiedUserId.current !== u.uid) {
            posthog.reset();
          }

          if (identifiedUserId.current !== u.uid) {
            const personProperties = {};
            if (u.email) personProperties.email = u.email;
            if (u.displayName) personProperties.name = u.displayName;
            posthog.identify(u.uid, personProperties);
            identifiedUserId.current = u.uid;
          }
        } else if (identifiedUserId.current) {
          posthog.reset();
          identifiedUserId.current = null;
        }
      }

      setUser(u);
      setInitialising(false);
    });
    return unsub;
  }, []);

  // Self-heal the streak when connectivity returns. recomputeStreakFromWins is
  // the source of truth (see wins.js), so an offline create whose optimistic bump
  // was skipped/failed is corrected on reconnect. Dynamically imported so wins.js
  // (and Firestore) stay off the boot path — it loads only when 'online' fires.
  useEffect(() => {
    const uid = user?.uid;
    if (!uid) return undefined;
    const onOnline = () => {
      import('../data/wins.js')
        .then((m) => m.recomputeStreakFromWins(uid))
        .catch(() => {});
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [user]);

  const value = useMemo(
    () => ({
      user,
      initialising,

      async signInWithEmail(email, password) {
        try {
          const cred = await signInWithEmailAndPassword(
            auth,
            String(email || '').trim(),
            password,
          );
          return { success: true, user: cred.user };
        } catch (err) {
          console.error('[Auth] Email sign-in failed:', err.code);
          return { success: false, error: friendlyError(err.code) };
        }
      },

      // Create an account with email/password, mirroring the vanilla
      // Auth.signUpWithEmail: create the auth user, set the display name, write
      // the userProfiles doc, then fire the welcome email fire-and-forget.
      // Navigation is the screen's job, not the context's.
      async signUpWithEmail(email, password, displayName) {
        try {
          const cred = await createUserWithEmailAndPassword(
            auth,
            String(email || '').trim(),
            password,
          );
          const name = String(displayName || '').trim();
          if (name) {
            try {
              await updateProfile(cred.user, { displayName: name });
            } catch (e) {
              // Non-fatal: the profile doc still records the name below.
              console.warn('[Auth] updateProfile(displayName) failed:', e);
            }
          }
          // Profile doc is required — surface a failure so the caller can react.
          const profileResult = await createUserProfile(cred.user, name);
          if (!profileResult.success) {
            return { success: false, error: 'Account created, but setting up your profile failed. Please sign in and try again.' };
          }
          // Fire-and-forget — signup must never be blocked or failed by this.
          triggerWelcomeEmail(cred.user.email, name || cred.user.displayName);
          return { success: true, user: cred.user };
        } catch (err) {
          console.error('[Auth] Sign up failed:', err.code);
          return { success: false, error: friendlyError(err.code) };
        }
      },

      async signInWithGoogle() {
        try {
          const provider = new GoogleAuthProvider();
          provider.setCustomParameters({ prompt: 'select_account' });
          const cred = await signInWithPopup(auth, provider);
          // A brand-new Google user has no userProfiles doc yet — create one,
          // same shape as email signup. Vanilla did this on isNewUser; the SPA
          // previously skipped it, leaving new Google users profile-less (a bug
          // that also affected the Login screen's Google button).
          const isNew = getAdditionalUserInfo(cred)?.isNewUser;
          if (isNew) {
            await createUserProfile(cred.user, cred.user.displayName);
            triggerWelcomeEmail(cred.user.email, cred.user.displayName);
          }
          return { success: true, user: cred.user };
        } catch (err) {
          console.error('[Auth] Google sign-in failed:', err.code);
          return { success: false, error: friendlyError(err.code) };
        }
      },

      async sendPasswordReset(email) {
        try {
          // Uses Firebase's built-in reset email (the vanilla app's fallback
          // path). The Cloud Function primary path (requestPasswordReset) is
          // not ported in Phase 0 — reset still works via this route.
          await sendPasswordResetEmail(auth, String(email || '').trim(), {
            url: 'https://app.bragsheet.net',
            handleCodeInApp: false,
          });
          return { success: true };
        } catch (err) {
          console.error('[Auth] Password reset failed:', err.code);
          return { success: false, error: friendlyError(err.code) };
        }
      },

      async signOut() {
        // Stage 2 (best-effort): drop THIS device's push subscription — and its
        // Firestore doc — BEFORE signing out, so on a shared device this user
        // stops receiving reminders once they log out. Reuses T3's
        // unsubscribeThisDevice (lazy-imported to stay off the boot path), which
        // both unsubscribes locally and deletes the doc while still authenticated.
        // Wrapped so ANY failure — import, network, rules, no subscription — is
        // swallowed and NEVER blocks or breaks the sign-out below.
        try {
          const { unsubscribeThisDevice } = await import('../data/push.js');
          await unsubscribeThisDevice();
        } catch (err) {
          console.warn('[Auth] Push cleanup on sign-out failed (continuing):', err);
        }

        try {
          await fbSignOut(auth);
          return { success: true };
        } catch (err) {
          console.error('[Auth] Sign out failed:', err);
          return { success: false, error: 'Sign out failed. Please try again.' };
        }
      },

      // IRREVERSIBLE account deletion. Mirrors the vanilla teardown order:
      //   1. Re-authenticate (Firebase requires a recent login for account
      //      deletion). Password users supply their password; Google users
      //      re-consent via a popup.
      //   2. Purge all Firestore data as the still-authenticated owner
      //      (data/account.js, dynamically imported so it stays off the boot
      //      path → wins/sections/profile; auditLog + mcpTokens are
      //      rules-immutable and left as-is — see that file).
      //   3. deleteUser() removes the Auth record. onAuthStateChanged then fires
      //      null and ProtectedRoute bounces to /login; the caller also
      //      navigates explicitly.
      // Purge runs BEFORE deleteUser on purpose (rules need the owner present).
      // If deleteUser somehow fails after a successful purge the account is
      // data-empty but still exists — surfaced as an error so the user can retry
      // (a retry re-auths and deletes the now-empty account cleanly).
      async deleteAccount({ password } = {}) {
        const current = auth.currentUser;
        if (!current) return { success: false, error: 'You are not signed in.' };
        const uid = current.uid;
        const providerId = current.providerData?.[0]?.providerId || 'password';

        // 1. Re-authenticate.
        try {
          if (providerId === 'password') {
            if (!password) {
              return { success: false, error: 'Please enter your password to confirm.', needsPassword: true };
            }
            const cred = EmailAuthProvider.credential(current.email, password);
            await reauthenticateWithCredential(current, cred);
          } else if (providerId === 'google.com') {
            const provider = new GoogleAuthProvider();
            provider.setCustomParameters({ prompt: 'select_account' });
            await reauthenticateWithPopup(current, provider);
          } else {
            return { success: false, error: 'Account deletion isn’t supported for this sign-in method.' };
          }
        } catch (err) {
          console.error('[Auth] Re-auth for delete failed:', err.code);
          return { success: false, error: friendlyError(err.code) };
        }

        // 2. Purge Firestore data while still authenticated as the owner.
        //    Lazy-import keeps the teardown code (and data/wins.js) off the boot
        //    path — it loads only when someone actually deletes their account.
        const { purgeUserData } = await import('../data/account.js');
        const purge = await purgeUserData(uid);
        if (!purge.success) {
          return { success: false, error: purge.error || 'Could not delete your data. Please try again.' };
        }

        // 3. Delete the Auth record.
        try {
          await deleteUser(current);
          return { success: true };
        } catch (err) {
          console.error('[Auth] deleteUser failed:', err.code);
          return { success: false, error: friendlyError(err.code) };
        }
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, initialising],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
